import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {base64ToFile, ImageCroppedEvent, ImageCropperComponent, ImageCropperModule, ImageTransform} from 'ngx-image-cropper';
import {ImageType} from '@services/resource/image-type';
import {CommonModule} from '@angular/common';
import {MarkantElementsModule} from '@markant/elements-angular';
import {MatSlider, MatSliderThumb} from '@angular/material/slider';

@Component({
  selector: 'app-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, MarkantElementsModule, ImageCropperModule, MatSlider, MatSliderThumb],
})
export class ImageEditorComponent {
  @Input() width = 300;
  @Input() height = 300;
  @Input() image: File | undefined;
  @Input() imageURL: string;
  @Input() uploadTarget: ImageType;
  @Input() startupName: string | undefined;
  @Input() productName: string | undefined;
  @Input() productCategory: string | undefined;

  imageType = ImageType;

  edit = false;
  @Output() cropEvent = new EventEmitter<Blob>();
  @Output() cancelEvent = new EventEmitter<boolean>();
  imageChangedEvent: any;
  croppedImage: any;
  @ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;

  canvasRotation = 0;
  rotation = 0;
  scale = 1;
  showCropper = false;
  maintainAspectRatio = true;
  transform: ImageTransform = {};

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
  }

  /**
   *
   * @param event
   * Event is triggered if the image gets cropped. If autocrop is set to true this event is triggered on every change
   * made in the editor.
   */
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  /**
   * This event is triggered if the image is successfully loaded into the cropper.
   */
  imageLoaded() {
    this.showCropper = true;
  }

  /**
   * Resets the current Image to the default state.
   */
  resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  /**
   * Zooms out of the canvas by 10%
   */
  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  /**
   * Zooms into the canvas by 10%
   */
  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  /**
   * Toggles the cropper to be fixed at a certain aspect ratio.
   */
  toggleMaintainAspectRatio() {
    this.maintainAspectRatio = !this.maintainAspectRatio;
  }

  /**
   * Rotates the image by 90° clockwise
   */
  rotateClockwise() {
    if (this.rotation + 90 == 360) {
      this.rotation = 0;
    } else {
      this.rotation += 90;
    }
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }

  /**
   * Rotates the image by 90° counterclockwise
   */
  rotateCounterClockwise() {
    if (this.rotation - 90 == 0) {
      this.rotation = 0;
    } else {
      this.rotation -= 90;
    }
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }

  /**
   * Emits the cropped image as a File through onCrop
   */
  initiateSave() {
    this.cropEvent.emit(base64ToFile(this.croppedImage));
  }

  /**
   * Emits true at onCancel to be able to destroy the component through *ngIf
   */
  cancel() {
    this.cancelEvent.emit(true);
  }

  /**
   * Changes the amount of zoom depending on the value of the event.
   * Default is 1.
   * @param event event.value to get the number on the slider
   */
  zoom(event: any) {
    this.scale = event.value / 100;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }
}
